"use client";
import Link from "next/link";
import Image from "next/legacy/image";
import isEmpty from "lodash/isEmpty";
import dynamic from "next/dynamic";

import classNames from "classnames";

import { FeatureFlagWrapper } from "@stadium/services/launch-darkly";
import { quickRecogniseWidgetFlag } from "@stadium/services/launch-darkly/constants";

import useRouter from "@stadium/hooks/lib/use-custom-route";

import StartButton from "@stadium/shared/src/components/recognise-widget/start-button";

import useStadiumSharedGlobalConfigContext from "@stadium/shared/src/hooks/use-global-context";

import {
  StickyHeader,
  HeaderContainer,
  Header,
  HeaderLeftContent,
} from "@stadium/ui/src/components/header";
import ImpersonationBanner from "@stadium/ui/src/components/impersonation-banner";
import { ContentLoader, ThreeProgressiveDots } from "@stadium/ui/src/components/loaders/loaders";
import WeglotDropdown from "@stadium/ui/src/components/weglot-dropdown";
import UserFundsDetail from "@stadium/shared/src/components/user-funds-detail";
import ProfileWeb from "@stadium/shared/src/components/header/profile-dropdown/web";
import LoggedOutWebStadiumHeader from "@stadium/shared/src/components/header/main/logged-out-web";

import { useJuneSoEventTracker } from "@stadium/services/june-analytics";

import { STADIUM } from "@stadium/ui/src/components/brands/all/constants";
import { DASHBOARD_ROUTE } from "@stadium/shared/src/constants/routes";
import { HEADER_LINKS_SERVICES, STADIUM_NAVBAR } from "@stadium/shared/src/constants/june-so";
import { STORES } from "@stadium/shared/src/constants/brands";
import AppGenericAnnouncementBar from "@stadium/ui/src/components/app-generic-announcement-bar";

import PersonalWorkspaceNudge from "@stadium/shared/src/components/personal-workspace-nudge";

import styles from "./main.module.scss";

const PrismicHeaderLinks = dynamic(() => import("../unauthenticated-dd-links"), {
  loading: () => <ContentLoader />,
});

const WebStadiumHeader = ({
  logout,
  profileData,
  isWeglotDropDown = false,
  isAuthenticated = true,
  isLoading = false,
  loginOptions,
  className = "",
  logoDetails = {},
  showHeaderNavLinks = false,
  headerLogoLink,
  showUserFunds = true,
  accessibilityEnabled = true,
  userDetails = {},
  application,
  isActivePackageUser,
  showRecogniseButton = false,
  isMarketingPage = false,
  personalWorkspaceNudge = null,
}) => {
  const { asPath } = useRouter();
  const { trackEvent } = useJuneSoEventTracker({
    services: HEADER_LINKS_SERVICES,
    elementPosition: STADIUM_NAVBAR,
  });

  const { site } = useStadiumSharedGlobalConfigContext();

  const renderProfileSection = () => {
    if (isLoading) {
      return <ThreeProgressiveDots />;
    }

    return (
      <ProfileWeb
        accountDropdownKey={`profile-dropdown-${asPath}`}
        logout={logout}
        profileData={profileData}
        showHeaderNavLinks={showHeaderNavLinks}
        accessibilityEnabled={accessibilityEnabled}
        isMarketingPage={isMarketingPage}
      />
    );
  };

  const renderDefaultLogo = () => (
    <Link
      href={headerLogoLink || DASHBOARD_ROUTE}
      passHref
      onClick={() => trackEvent({ target: "stadium-logo" })}
    >
      <Image src={STADIUM.logo2} height={32} width={132} alt={STADIUM.imgAlt} />
    </Link>
  );

  const renderCompanyLogo = () => (
    <Link href={logoDetails.url} passHref>
      <Image src={logoDetails.logo} height={32} width={132} alt={logoDetails.name} />
    </Link>
  );

  const renderLeftLogoLinks = () => (
    <HeaderLeftContent
      className={`${styles.headerDashBoardSec} ${
        isMarketingPage ? styles.stadiumLogoBlockMarketing : ""
      }`}
    >
      <span className={`${styles.headerLogoHolder} ${styles.stadiumLogoBlock}`}>
        {isEmpty(logoDetails) ? renderDefaultLogo() : renderCompanyLogo()}
      </span>
    </HeaderLeftContent>
  );

  return isAuthenticated ? (
    <HeaderContainer className={styles.loggedInPrimaryHeader}>
      <StickyHeader rootClass={`${styles.stickyHeaderWeb} ${className}`}>
        <ImpersonationBanner currentUsername={profileData?.authUsername} />
        <AppGenericAnnouncementBar application={application} options={{ isActivePackageUser }} />
        <Header level={1}>
          <div
            className={`${styles.primaryHeaderRow} ${
              isMarketingPage ? styles.primaryHeaderRowNew : ""
            }`}
          >
            {renderLeftLogoLinks()}
            <div
              className={`${styles.headerTabsSection} ${
                isMarketingPage ? styles.headerTabsSectionNew : ""
              }`}
            >
              {(site === STORES || showHeaderNavLinks) && (
                <PrismicHeaderLinks isMarketingPage={isMarketingPage} />
              )}
            </div>
            <div
              className={classNames({
                [styles.rightSectionLink]: !isMarketingPage,
                [styles.rightSectionLinkMarketing]: isMarketingPage,
              })}
            >
              <FeatureFlagWrapper flag={quickRecogniseWidgetFlag}>
                {showRecogniseButton && <StartButton />}
              </FeatureFlagWrapper>
              {isWeglotDropDown && <WeglotDropdown />}
              {showUserFunds && <UserFundsDetail userDetails={userDetails} />}
              <div className={styles.personalWorkspaceNudgeAnchor}>
                <PersonalWorkspaceNudge variant="web" show={personalWorkspaceNudge} />
                {renderProfileSection()}
              </div>
            </div>
          </div>
        </Header>
      </StickyHeader>
    </HeaderContainer>
  ) : (
    <LoggedOutWebStadiumHeader
      isWeglotDropDown={isWeglotDropDown}
      isLoading={isLoading}
      loginOptions={loginOptions}
      headerLogoLink={headerLogoLink}
      isMarketingPage={isMarketingPage}
    />
  );
};

export default WebStadiumHeader;
